import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Building2,
  Users,
  Briefcase,
  ClipboardList,
  UserCog,
  BarChart3,
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Settings,
  KeyRound,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import api from "../api/axios";

import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import UiText from './UiText';
import NotificationCenter from './NotificationCenter';
import './Sidebar.css';

const Sidebar = ({ activePage, isCollapsed, setIsCollapsed }) => {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [badgePulse, setBadgePulse] = useState(false);
  const prevUnreadCountRef = useRef(0);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const sections = useMemo(() => {
    const baseItems = [
      { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/dashboard" },
      { id: "companies", label: "Companies", icon: Building2, path: "/companies" },
      { id: "customers", label: "Customers", icon: Users, path: "/customers" },
      { id: "deals", label: "Deals", icon: Briefcase, path: "/deals" },
    ];

    // Hide pipeline link for basic `user` role
    if (user?.role !== 'user') {
      baseItems.push({ id: "pipeline", label: "Pipeline", icon: ClipboardList, path: "/pipeline" });
    }

    const base = [{ title: "MAIN", items: baseItems }];

    if (user?.role === 'admin') {
      base.push({
        title: "ADMIN",
        items: [
          { id: "admin-users", label: "User Management", icon: UserCog, path: "/admin/users" },
          { id: "admin-analysis", label: "Analysis & Reports", icon: BarChart3, path: "/admin/analysis-reports" },
          { id: "admin-configuration", label: "Configuration", icon: Settings, path: "/admin/configuration" },
          { id: "admin-mcp", label: "MCP Access", icon: KeyRound, path: "/admin/mcp" },
        ],
      });
    }

    return base;
  }, [user?.role]);

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(" ");
    return parts[0][0] + (parts[1]?.[0] || "");
  };

  const handleLogout = () => setShowConfirm(true);
  const handleConfirmLogout = () => {
    logout();
    navigate("/");
  };

  const fetchUnreadCount = async () => {
    try {
      // Lightweight indexed count query - avoids fetching/filtering the full
      // notification list every 30s (which doesn't scale as notifications grow).
      const response = await api.get('/api/notifications/unread-count');
      const unread = response.data?.unread_count ?? 0;
      if (unread > prevUnreadCountRef.current) {
        setBadgePulse(true);
        setTimeout(() => setBadgePulse(false), 600);
      }
      prevUnreadCountRef.current = unread;
      setUnreadCount(unread);
    } catch (err) {
      if (err.response?.status !== 401) {
        console.error('Error fetching unread count:', err);
      }
    }
  };

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <div
        className={`h-screen text-white flex flex-col ${isCollapsed ? "w-[76.8px]" : "w-64"} fixed left-0 top-0 z-50 transition-all duration-500 ease-in-out overflow-hidden`}
        style={{ background: "linear-gradient(180deg,#071739 0%, #041233 100%)" }}
      >
        {/* Toggle Sidebar Button */}
        <div className="flex justify-end p-3">
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-white bg-white/5 hover:bg-white/10 p-2 rounded-full transition-shadow shadow-sm"
            data-tooltip-id="sidebar-tooltip"
            data-tooltip-content={isCollapsed ? "Expand" : "Collapse"}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Branding */}
        <div className="px-4 pt-6">
            {isCollapsed ? (
            <UiText variant="heading" as="h1" className="text-center">
              <span className="text-white">D</span>
              <span className="text-red-400">M</span>
            </UiText>
          ) : (
            <>
              <UiText variant="heading" as="h1">Deal <span className="text-red-400">Manager</span></UiText>
              <span className="inline-block mt-1 text-xs px-2 py-0.5 bg-blue-700 rounded-md">
                v2.0
              </span>
            </>
          )}
        </div>

        {/* Menu Sections */}
        <div className="flex-1 overflow-y-auto scrollbar-hide px-3 mt-6"> 
          {sections.map((sec, idx) => (
            <div key={idx} className={`mb-6 ${sec.title === "ADMIN" ? "border-t border-blue-800 pt-4" : ""}`}>
              <h3
                className={`flex items-center gap-2 text-xs font-semibold text-gray-400 mb-2 tracking-wide ${
                  isCollapsed ? "hidden" : ""
                }`}
              >
                {sec.title}
                {sec.title === "ADMIN" && (
                  <span className="text-[10px] px-2 py-0.5 bg-red-600 text-white rounded-full">ADMIN</span>
                )}
              </h3>

              <div className="flex flex-col">
                {sec.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activePage === item.id;

                  return (
                    <Link key={item.id} to={item.path} className="block mb-3 last:mb-0">
                      <div
                        className={`relative group flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-all transform ${
                          isActive
                            ? "bg-gradient-to-r from-blue-700 to-blue-600 text-white shadow-lg"
                            : "text-gray-300 hover:bg-blue-900 hover:text-white hover:translate-x-1"
                        }`}
                      >
                        {isActive && (
                          <span className="absolute left-0 top-0 h-full w-1 bg-blue-400 rounded-r-lg"></span>
                        )}

                        {/* Icon with Tooltip */}
                        <Icon
                          size={18}
                          className={isActive ? "text-white" : "text-gray-300 group-hover:text-white"}
                          data-tooltip-id="sidebar-tooltip"
                          data-tooltip-content={item.label}
                        />

                        {/* Hide label when collapsed */}
                        <span className={`text-sm font-medium whitespace-nowrap flex-1 ${isCollapsed ? "hidden" : ""}`}>
                          {item.label}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Notifications */}
        <div className="px-3 mb-3">
          <button
            onClick={() => setNotificationOpen(true)}
            className="relative group w-full flex items-center gap-3 px-3 py-2 text-gray-300 hover:bg-blue-900 hover:text-white rounded-lg transition-all transform hover:translate-x-1 cursor-pointer"
            data-tooltip-id="sidebar-tooltip"
            data-tooltip-content={isCollapsed ? "Notifications" : ""}
          >
            <div className="relative">
              <Bell size={18} className="text-gray-300 group-hover:text-white" />
              {unreadCount > 0 && (
                <span className={`absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center ring-2 ring-[#071739] font-semibold ${badgePulse ? 'notification-badge-pulse' : ''}`}>
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>
            <span className={`text-sm font-medium whitespace-nowrap ${isCollapsed ? "hidden" : ""}`}>
              Notifications
            </span>
          </button>
        </div>

        {/* User Card */}
        <div className="p-4 relative" ref={menuRef}>
          <div
            onClick={() => setMenuOpen(!menuOpen)}
            className={`flex items-center justify-center shadow hover:bg-blue-900 transition cursor-pointer ${
              isCollapsed ? "w-12 h-12 rounded-full bg-[#0D1B50]" : "bg-[#0D1B50] p-3 rounded-xl gap-3"
            }`}
            data-tooltip-id="sidebar-tooltip"
            data-tooltip-content={isCollapsed ? user?.full_name : ""}
          >
            <div
              className={`${
                isCollapsed ? "w-8 h-8" : "w-10 h-10"
              } rounded-full bg-blue-700 flex items-center justify-center text-sm font-semibold`}
            >
              {getInitials(user?.full_name || "User")}
            </div>

            {!isCollapsed && (
              <>
                <div className="flex-1">
                  <UiText variant="section" as="p" className="font-semibold leading-none text-white">{user?.full_name}</UiText>
                  <UiText variant="label" as="p" className="text-blue-300">{user?.role === 'admin' ? "Administrator" : "User"}</UiText>
                </div>
                <ChevronDown 
                  size={18} 
                  className={`text-gray-300 transition-transform duration-200 ${menuOpen ? "rotate-180" : ""}`} 
                />
              </>
            )}
          </div>

          {menuOpen && (
            <div className={`${
              isCollapsed 
                ? "fixed bottom-4 left-[92.8px] z-[100]" 
                : "absolute bottom-20 left-4 z-50"
            } w-56 bg-white text-black rounded-xl shadow-xl p-4`}>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-700 flex items-center justify-center text-sm font-semibold text-white">
                  {getInitials(user?.full_name)}
                </div>
                <div>
                  <p className="font-semibold">{user?.full_name}</p>
                  <UiText variant="label" as="p">{user?.email}</UiText>
                </div>
              </div>

              <button
                onClick={handleLogout}
                className="w-full text-left text-red-600 font-medium py-2 px-2 rounded hover:bg-red-50"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Logout Confirm Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[1000]">
          <div className="bg-white p-8 rounded-2xl w-[400px] shadow-xl">
            <UiText variant="title" as="h2" className="mb-6">Are you sure, you want to logout?</UiText>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowConfirm(false)} className="px-5 py-2 border rounded-lg">
                Cancel
              </button>
              <button onClick={handleConfirmLogout} className="px-5 py-2 bg-red-600 text-white rounded-lg">
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Center Modal */}
      <NotificationCenter 
        isOpen={notificationOpen} 
        onClose={() => {
          setNotificationOpen(false);
          fetchUnreadCount(); // Refresh count when modal closes
        }}
      />

      {/* Tooltip (simple style) - always mounted so data-tooltip-id attributes work consistently */}
      <Tooltip
        id="sidebar-tooltip"
        place={isCollapsed ? "right" : "top"}
        offset={12}
        className="!bg-black !text-white !text-xs px-1 py-1 !rounded-lg !shadow-none !border-none !z-[999999]"
      />
    </>
  );
};

export default Sidebar;